/**
 * ReportPendingPage - Página de Reporte de Cheques Pendientes
 * Muestra todos los cheques pendientes con alertas visuales por vencimiento
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { PageHeader, LoadingSpinner } from '../components/common';
import { useNotification } from '../context/NotificationContext';
import { parseApiDate } from '../utils/formatters';
import reporteService from '../services/reporteService';
import catalogoService from '../services/catalogoService';

const ReportPendingPage = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [bancos, setBancos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [filters, setFilters] = useState({
    bancoId: '',
    clienteId: '',
    fechaDesde: '',
    fechaHasta: '',
  });
  const { showError, showSuccess } = useNotification();

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [reportResult, bancosResult, clientesResult] = await Promise.all([
        reporteService.getPendingReport(),
        catalogoService.getBancos(),
        catalogoService.getClientes(),
      ]);

      // Mapear datos del backend
      const mappedReport = mapReportData(reportResult);
      setReportData(mappedReport);
      setBancos(bancosResult.data || bancosResult);
      setClientes(clientesResult.data || clientesResult);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      showError('Error al cargar el reporte de pendientes');
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      const result = await reporteService.getPendingReport(filters);
      const mappedReport = mapReportData(result);
      setReportData(mappedReport);
    } catch (error) {
      console.error('Error al cargar reporte:', error);
      showError('Error al cargar el reporte de pendientes');
    } finally {
      setLoading(false);
    }
  };

  const mapReportData = (result) => {
    const data = result.data || result;
    return {
      totalCheques: data.totales?.cantidad || 0,
      montoTotalPendiente: data.totales?.monto_total || 0,
      cheques: data.cheques || [],
    };
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    loadReport();
  };

  const handleClearFilters = () => {
    setFilters({
      bancoId: '',
      clienteId: '',
      fechaDesde: '',
      fechaHasta: '',
    });
    loadInitialData();
  };

  const handleExportExcel = async () => {
    try {
      const blob = await reporteService.exportReportToExcel('pendientes', filters);
      reporteService.downloadReport(blob, `reporte-pendientes.xlsx`);
      showSuccess('Reporte exportado a Excel correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      showError('Error al exportar el reporte a Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const blob = await reporteService.exportReportToPDF('pendientes', filters);
      reporteService.downloadReport(blob, `reporte-pendientes.pdf`);
      showSuccess('Reporte exportado a PDF correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      showError('Error al exportar el reporte a PDF');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(value || 0);
  };

  // Formatear una fecha del API (DATEONLY) sin desfase de zona horaria
  const formatFecha = (fecha) => {
    const parsed = parseApiDate(fecha);
    return parsed ? parsed.toLocaleDateString('es-AR') : '-';
  };

  // Calcular días transcurridos desde la fecha de pago
  const getDaysElapsed = (fechaPago) => {
    const paymentDate = parseApiDate(fechaPago);
    if (!paymentDate) return 0;

    // Se comparan solo las fechas (sin hora) para evitar desfases
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    paymentDate.setHours(0, 0, 0, 0);

    const diffTime = today - paymentDate;
    return Math.round(diffTime / (1000 * 60 * 60 * 24));
  };

  // Obtener estilo de alerta según días transcurridos
  const getAlertStyle = (fechaPago) => {
    const days = getDaysElapsed(fechaPago);

    if (days > 0) {
      // Vencido
      return {
        color: 'error',
        icon: <ErrorIcon />,
        label: 'Vencido',
        bgColor: '#ffebee',
      };
    } else if (days >= -3 && days <= 0) {
      // Próximo a vencer (3 días o menos)
      return {
        color: 'warning',
        icon: <WarningIcon />,
        label: 'Próximo a vencer',
        bgColor: '#fff3e0',
      };
    } else {
      // Normal
      return {
        color: 'success',
        icon: <CheckCircleIcon />,
        label: 'Normal',
        bgColor: '#e8f5e9',
      };
    }
  };

  // Agrupar cheques por estado de alerta
  const getChequesGrouped = () => {
    if (!reportData?.cheques) return { vencidos: [], proximosVencer: [], normales: [] };

    const vencidos = [];
    const proximosVencer = [];
    const normales = [];

    reportData.cheques.forEach((cheque) => {
      const days = getDaysElapsed(cheque.fecha_pago);
      if (days > 0) {
        vencidos.push(cheque);
      } else if (days >= -3 && days <= 0) {
        proximosVencer.push(cheque);
      } else {
        normales.push(cheque);
      }
    });

    return { vencidos, proximosVencer, normales };
  };

  if (loading && !reportData) {
    return <LoadingSpinner message="Cargando reporte de pendientes..." />;
  }

  const chequesGrouped = getChequesGrouped();

  return (
    <Box>
      <PageHeader
        title="Cheques Pendientes"
        subtitle="Seguimiento de cheques pendientes de depósito con alertas de vencimiento"
        breadcrumbs={[
          { label: 'Inicio', path: '/dashboard' },
          { label: 'Reportes', path: '/reportes' },
          { label: 'Pendientes' },
        ]}
      />

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtros
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Banco</InputLabel>
              <Select
                value={filters.bancoId}
                label="Banco"
                onChange={(e) => handleFilterChange('bancoId', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {bancos.map((banco) => (
                  <MenuItem key={banco.id} value={banco.id}>
                    {banco.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Cliente</InputLabel>
              <Select
                value={filters.clienteId}
                label="Cliente"
                onChange={(e) => handleFilterChange('clienteId', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {clientes.map((cliente) => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    {cliente.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Fecha Desde"
              type="date"
              size="small"
              value={filters.fechaDesde}
              onChange={(e) => handleFilterChange('fechaDesde', e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Fecha Hasta"
              type="date"
              size="small"
              value={filters.fechaHasta}
              onChange={(e) => handleFilterChange('fechaHasta', e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
                fullWidth
              >
                Filtrar
              </Button>
              <Button variant="outlined" onClick={handleClearFilters} disabled={loading}>
                Limpiar
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            disabled={!reportData || loading}
          >
            Exportar Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportPDF}
            disabled={!reportData || loading}
          >
            Exportar PDF
          </Button>
        </Box>
      </Paper>

      {reportData && (
        <>
          {/* Resumen con Total Pendiente */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: '#e3f2fd' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Pendiente
                  </Typography>
                  <Typography variant="h4">
                    {reportData.totalCheques || 0}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(reportData.montoTotalPendiente || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: '#ffebee' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Vencidos
                  </Typography>
                  <Typography variant="h4" color="error">
                    {chequesGrouped.vencidos.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatCurrency(
                      chequesGrouped.vencidos.reduce((sum, ch) => {
                        const monto = parseFloat(ch.monto);
                        return sum + (isNaN(monto) ? 0 : monto);
                      }, 0)
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: '#fff3e0' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Próximos a Vencer
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {chequesGrouped.proximosVencer.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatCurrency(
                      chequesGrouped.proximosVencer.reduce((sum, ch) => {
                        const monto = parseFloat(ch.monto);
                        return sum + (isNaN(monto) ? 0 : monto);
                      }, 0)
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: '#e8f5e9' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Normales
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {chequesGrouped.normales.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatCurrency(
                      chequesGrouped.normales.reduce((sum, ch) => {
                        const monto = parseFloat(ch.monto);
                        return sum + (isNaN(monto) ? 0 : monto);
                      }, 0)
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Alertas */}
          {chequesGrouped.vencidos.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Atención:</strong> Hay {chequesGrouped.vencidos.length} cheque(s) vencido(s)
                que requieren atención inmediata.
              </Typography>
            </Alert>
          )}

          {chequesGrouped.proximosVencer.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Aviso:</strong> Hay {chequesGrouped.proximosVencer.length} cheque(s)
                próximo(s) a vencer en los próximos 3 días.
              </Typography>
            </Alert>
          )}

          {/* Tabla de Cheques Pendientes */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Estado</TableCell>
                    <TableCell>Número</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Banco</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>Fecha Pago</TableCell>
                    <TableCell align="center">Días</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.cheques?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No hay cheques pendientes
                      </TableCell>
                    </TableRow>
                  ) : (
                    reportData.cheques?.map((cheque) => {
                      const alertStyle = getAlertStyle(cheque.fecha_pago);
                      const daysElapsed = getDaysElapsed(cheque.fecha_pago);

                      return (
                        <TableRow
                          key={cheque.id}
                          sx={{ bgcolor: alertStyle.bgColor }}
                          hover
                        >
                          <TableCell>
                            <Chip
                              icon={alertStyle.icon}
                              label={alertStyle.label}
                              color={alertStyle.color}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <strong>{cheque.numero_cheque}</strong>
                          </TableCell>
                          <TableCell>{cheque.cliente?.nombre || '-'}</TableCell>
                          <TableCell>{cheque.banco?.nombre || '-'}</TableCell>
                          <TableCell align="right">
                            <strong>{formatCurrency(cheque.monto)}</strong>
                          </TableCell>
                          <TableCell>
                            {formatFecha(cheque.fecha_pago)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={
                                daysElapsed > 0
                                  ? `+${daysElapsed}`
                                  : daysElapsed === 0
                                  ? 'Hoy'
                                  : `${daysElapsed}`
                              }
                              color={
                                daysElapsed > 0
                                  ? 'error'
                                  : daysElapsed >= -3
                                  ? 'warning'
                                  : 'default'
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {!reportData && !loading && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Cargando información de cheques pendientes...
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ReportPendingPage;
